import { isProductId, type ProductId } from '@bb/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { loadCurrentUser } from '@/features/auth/use-current-user';
import { Turnstile } from '@/features/public-booking/turnstile';
import { api } from '@/lib/api';
import { ensureNamespace } from '@/lib/i18n';

export interface LoginSearch {
  product?: ProductId;
  returnTo?: string;
  error?: string;
  provider?: string;
  /**
   * Pre-fills the email-link field. Set when the app already knows which
   * address is wanted — a workspace invitation is addressed to one inbox, and
   * making somebody retype it is how they end up signing in as the wrong
   * account and wondering why the invitation will not take.
   */
  email?: string;
}

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    ...(isProductId(search.product) ? { product: search.product } : {}),
    ...(typeof search.returnTo === 'string' && search.returnTo.startsWith('/')
      ? { returnTo: search.returnTo }
      : {}),
    ...(typeof search.error === 'string' ? { error: search.error } : {}),
    ...(typeof search.provider === 'string' ? { provider: search.provider } : {}),
    ...(typeof search.email === 'string' ? { email: search.email.slice(0, 254) } : {}),
  }),
  beforeLoad: async ({ context, search }) => {
    const user = await loadCurrentUser(context.queryClient);
    if (user) throw redirect({ to: search.returnTo ?? '/app' });
    await ensureNamespace('auth');
  },
  component: LoginPage,
});

const KNOWN_ERRORS = new Set([
  'provider_mismatch',
  'state_invalid',
  'provider_error',
  'email_unverified',
]);

function LoginPage() {
  const { product, returnTo, error, provider, email: invited } = Route.useSearch();
  const { t } = useTranslation(['auth', 'common']);

  const params = new URLSearchParams();
  if (product) params.set('product', product);
  if (returnTo) params.set('returnTo', returnTo);
  params.set('tz', Intl.DateTimeFormat().resolvedOptions().timeZone);
  const query = `?${params.toString()}`;

  const errorKey = error && KNOWN_ERRORS.has(error) ? error : error ? 'provider_error' : null;
  const providerName = provider ? t(`auth:providers.${provider}`, { defaultValue: provider }) : '';

  return (
    <main className="mx-auto flex min-h-full max-w-sm flex-col justify-center gap-6 px-4">
      <div>
        <h1 className="text-2xl font-semibold">{t('auth:login.title')}</h1>
        <p className="mt-1 text-sm text-neutral-600">{t('auth:login.subtitle')}</p>
        {product ? (
          <p className="mt-2 text-sm">
            {t('auth:login.signingUpFor', { product: t(`common:products.${product}.name`) })}
          </p>
        ) : null}
      </div>
      {errorKey ? (
        <p
          role="alert"
          className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {t(`auth:errors.${errorKey}`, { provider: providerName })}
        </p>
      ) : null}
      <div className="flex flex-col gap-2">
        <a href={`/api/auth/google/start${query}`} className={btn}>
          {t('auth:login.google')}
        </a>
        <a href={`/api/auth/microsoft/start${query}`} className={btn}>
          {t('auth:login.microsoft')}
        </a>
      </div>
      {/*
        Below the providers, not beside them: most people have one of the two,
        and this is the way in for those who do not — and the way back in when a
        provider is having a bad day.
      */}
      <EmailLink returnTo={returnTo} initialEmail={invited} />

      <p className="text-xs text-neutral-500">
        <Trans
          i18nKey="auth:login.terms"
          components={[
            <Link key="terms" to="/terms" className="underline" />,
            <Link key="privacy" to="/privacy" className="underline" />,
          ]}
        />
      </p>
    </main>
  );
}

/**
 * Asking for a sign-in link.
 *
 * The confirmation is the same whatever happened, because the server's answer
 * is: it says a link is on its way to an address that can sign in, and never
 * whether this one can. Saying anything more here would give away from the
 * browser what the API refuses to give away.
 */
function EmailLink({
  returnTo,
  initialEmail,
}: {
  returnTo: string | undefined;
  initialEmail: string | undefined;
}) {
  const { t } = useTranslation(['auth', 'common']);
  const [email, setEmail] = useState(initialEmail ?? '');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState<string | null>(null);
  /*
    Only to learn whether there is a challenge to draw. Null means Turnstile is
    not configured on this deployment, and the form behaves as it did before.
  */
  const { data: config } = useQuery({
    queryKey: ['auth', 'config'],
    queryFn: () => api<{ turnstileSiteKey: string | null }>('/auth/config'),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const siteKey = config?.turnstileSiteKey ?? null;

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api<{ ok: true }>('/auth/email/start', {
        method: 'POST',
        body: {
          email: email.trim(),
          ...(returnTo ? { returnTo } : {}),
          ...(captcha ? { captchaToken: captcha } : {}),
        },
      });
    } catch {
      // Deliberately swallowed: a failure here would say more about the address
      // than a success does.
    }
    setSent(true);
    setBusy(false);
  };

  if (sent) {
    return (
      <p
        role="status"
        className="rounded border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm"
      >
        {t('auth:login.emailSent')}
      </p>
    );
  }

  return (
    <form onSubmit={send} className="flex flex-col gap-2">
      <label className="text-sm text-neutral-600" htmlFor="login-email">
        {t('auth:login.emailDivider')}
      </label>
      <input
        id="login-email"
        type="email"
        required
        autoComplete="email"
        value={email}
        onChange={(ev) => setEmail(ev.target.value)}
        placeholder={t('auth:login.emailLabel')}
        className="min-h-12 w-full rounded border border-neutral-300 px-3 py-2 text-sm"
      />
      {/*
        Invisible to almost everyone: `interaction-only` shows a checkbox only
        when Cloudflare is unsure. Anyone it does stop still has the two buttons
        above, so a challenge that will not load is a nuisance rather than a
        locked door.
      */}
      {siteKey ? <Turnstile siteKey={siteKey} onToken={setCaptcha} /> : null}
      <button type="submit" disabled={busy} className={`${btn} disabled:opacity-40`}>
        {t('auth:login.emailSend')}
      </button>
    </form>
  );
}

const btn =
  'flex min-h-12 items-center justify-center rounded border border-neutral-300 bg-white px-4 py-2 text-center text-sm font-medium hover:bg-neutral-50';
